import VideoPlayer from "@dana/Components/VideoPlayer";
import PrevArrowIcon from "@dana/Icons/PrevArrowIcon";
import { themeBackgrounds } from "@dana/MobiusOverrides/styles/colors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    LinkFieldValue,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useState } from "react";
import { css } from "styled-components";


const enum fields {
    heading = "heading",
    subText = "subText",
    description = "description",
    image = "image",
    imageAltText = "imageAltText",
    video = "video",
    videoCaption = "videoCaption",
    watchVideoLabel = "watchVideoLabel",
    backLabel = "backLabel",
    selectReadyShaftTypeUrl = "selectReadyShaftTypeUrl",
    shortCoupleLabel = "shortCoupleLabel",
    shortCoupleUrl = "shortCoupleUrl",
    interAxleLabel = "interAxleLabel",
    interAxleUrl = "interAxleUrl",
}

interface OwnProps extends PageProps {
    fields: {
        [fields.heading]: string;
        [fields.subText]: string;
        [fields.description]: string;
        [fields.image]: string;
        [fields.imageAltText]: string;
        [fields.video]: LinkFieldValue;
        [fields.videoCaption]: LinkFieldValue;
        [fields.watchVideoLabel]: string;
        [fields.backLabel]: string;
        [fields.selectReadyShaftTypeUrl]: LinkFieldValue;
        [fields.shortCoupleLabel]: string;
        [fields.shortCoupleUrl]: LinkFieldValue;
        [fields.interAxleLabel]: string;
        [fields.interAxleUrl]: LinkFieldValue;
    };
}

export interface ShortCoupleSelectionStyles {
    wrapper?: InjectableCss;
    backButton?: ButtonPresentationProps;
    heading?: TypographyPresentationProps;
    subText?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    contentWrapper?: InjectableCss;
    actionWrapper?: InjectableCss;
    image?: ImgProps;
    videoModal?: ModalPresentationProps;
}

export const styles: ShortCoupleSelectionStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 70%;
            gap: 24px;
            margin: 48px auto 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                                    width: 100%;
                                                `,
                        css`
                                                    width: 100%;
                                                `,
                        css`
                                                    width: 100%;
                                                `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    backButton: {
        variant: "tertiary",
        css: css`
            align-self: flex-start;
            text-align: left;
        `,
        icon: {
            src: PrevArrowIcon,
            position: "left",
        },
    },
    heading: {
        variant: "h3",
        css: css`
            margin-bottom: 0;
            text-align: center;
        `,
    },
    subText: {
        variant: "h4",
        css: css`
            text-align: center;
            margin-bottom: 0;
        `,
    },
    description: {
        variant: "h4",
        css: css`
            text-align: center;
            margin-bottom: 0;
        `,
    },
    contentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            border: 1px solid ${themeBackgrounds.lightGray};
            border-radius: 4px;
            padding: 16px;
            box-sizing: border-box;

            & > *:not(:last-child) {
                border-bottom: 1px solid ${themeBackgrounds.lightGray};
                padding-bottom: 16px;
                margin-bottom: 16px;
            }
        `,
    },
    image: {
        css: css`
        width: 100%;
        height: auto;
        max-height: 480px;
        object-fit: contain;
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-height: 180px;
                        `,
                        css`
                            max-height: 220px;
                        `,
                        css`
                            max-height: 260px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
    `,
    },
    actionWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            width: 100%;
            gap: 16px;
            
            & > * {
                flex: 1 1 0;
            }
        `,
    },
    videoModal: {
        cssOverrides: {
            modalContent: css`
            video {
                width: 100%;
                height: auto;
                max-height: 70vh;
                display: block;
            }
        `,
        },
        sizeVariant: "large",
    },
};

const EZIDShortCoupleSelectionPage = ({ id, fields }: OwnProps) => {

    const [videoModalIsOpen, setVideoModalIsOpen] = useState(false);
    const videoLink = useGetLink(fields.video);
    const captionLink = useGetLink(fields.videoCaption);
    const shortCoupleLink = useGetLink(fields.shortCoupleUrl);
    const interAxleLink = useGetLink(fields.interAxleUrl);
    const selectReadyShaftTypeUrl = useGetLink(fields.selectReadyShaftTypeUrl);

    const history = useHistory();
    const { isInShell } = useShellContext();

    const onButtonClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const videoModalCloseHandler = () => {
        setVideoModalIsOpen(false);
    };

    return (
        <Page>
            <Zone contentId={id} zoneName="Content" requireRows />
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <Button
                    {...styles.backButton}
                    onClick={() => onButtonClick(selectReadyShaftTypeUrl?.url)}>
                    {fields.backLabel}
                </Button>
                <Typography {...styles.heading} as="h3">
                    {fields.heading}
                </Typography>
                <Typography {...styles.subText} as="h4">
                    {fields.subText}
                </Typography>
                <Typography {...styles.description} as="h4">
                    {fields.description}
                </Typography>
                <StyledWrapper {...styles.contentWrapper}>
                    {fields.image && (
                        <Img src={fields.image} altText={fields.imageAltText || ''} {...styles.image} />
                    )}
                    <StyledWrapper {...styles.actionWrapper}>
                        <Button variant="primary" onClick={() => onButtonClick(shortCoupleLink?.url)}>
                            {fields.shortCoupleLabel}
                        </Button>
                        <Button variant="primary" onClick={() => onButtonClick(interAxleLink?.url)}>
                            {fields.interAxleLabel}
                        </Button>
                    </StyledWrapper>
                    {videoLink?.url && (
                        <Button variant="primary" onClick={() => setVideoModalIsOpen(true)}>
                            {fields.watchVideoLabel}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <Modal
                {...styles.videoModal}
                id="shortCoupleSelectionVideoModal"
                isOpen={videoModalIsOpen}
                handleClose={videoModalCloseHandler}>
                <StyledWrapper>
                    <VideoPlayer
                        videoUrl={videoLink?.url || ''}
                        videoCaptionUrl={captionLink?.url || ''}
                    />
                </StyledWrapper>
            </Modal>
        </Page>
    )
};

const pageModule: PageModule = {
    component: EZIDShortCoupleSelectionPage,
    definition: {
        displayName: "EZ-ID Short Couple / Interaxle Shaft Selector Page",
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "Content",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "TextField",
                displayName: "Page Heading",
                defaultValue: "Spicer ReadyShaft Visual Search",
            },
            {
                fieldType: "Translatable",
                name: fields.subText,
                editorTemplate: "TextField",
                displayName: "Page Sub Text",
                defaultValue: "Short Couple / Interaxle Shaft Selector",
            },
            {
                fieldType: "Translatable",
                name: fields.description,
                editorTemplate: "TextField",
                displayName: "Page Description",
                defaultValue: "Does Your Driveshaft Have Tubing?",
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                displayName: "Page Image",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.imageAltText,
                editorTemplate: "TextField",
                displayName: "Page Image Alt Text",
                defaultValue: "",
            },
            {
                name: fields.video,
                fieldType: "Translatable",
                displayName: "Page Video",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
            },
            {
                name: fields.videoCaption,
                fieldType: "Translatable",
                displayName: "Page Video Caption",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
            },
            {
                fieldType: "Translatable",
                name: fields.watchVideoLabel,
                editorTemplate: "TextField",
                displayName: "Watch Video Label",
                defaultValue: "Watch The Video",
            },
            {
                fieldType: "Translatable",
                name: fields.backLabel,
                editorTemplate: "TextField",
                displayName: "Back Button Label",
                defaultValue: "Back",
            },
            {
                name: fields.selectReadyShaftTypeUrl,
                fieldType: "Translatable",
                displayName: "Select Ready Shaft Type URL",
                tooltip: "URL to navigate back to the 'Select ReadyShaft Type' page.",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
            },
            {
                fieldType: "Translatable",
                name: fields.shortCoupleLabel,
                editorTemplate: "TextField",
                displayName: "Short Couple Label",
                tooltip: "The answer for the question 'Does Your Driveshaft Have Tubing?'",
                defaultValue: "No",
            },
            {
                name: fields.shortCoupleUrl,
                fieldType: "Translatable",
                displayName: "Short Couple URL",
                tooltip: "URL to navigate to the 'Short Couple' page when the answer is 'No'.",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
            },
            {
                fieldType: "Translatable",
                name: fields.interAxleLabel,
                editorTemplate: "TextField",
                displayName: "Inter Axle Label",
                tooltip: "The answer for the question 'Does Your Driveshaft Have Tubing?'",
                defaultValue: "Yes",
            },
            {
                name: fields.interAxleUrl,
                fieldType: "Translatable",
                displayName: "Inter Axle URL",
                tooltip: "URL to navigate to the 'Inter Axle' page when the answer is 'Yes'.",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
            },
        ],
    },
};

export default pageModule;
